import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
  LabelList
} from 'recharts';

import { useAuth } from '../../../../../providers/AuthContext';
import Spinner from '../../../../../components/Spinner';
import { getWidgetById } from '../../../../../services/apiWidgests';
import { useDashboards } from '../../../../../providers/DashboardContext';
import useWidgetRuntime from '../../../../../hooks/useWidgetRuntime';
import { useAppContext } from '../../../../../providers/AppContext';

const normalizeGroup = (g) => ({
  label: g.group,
  quality: g.quality ?? 0
});

const QualityWidget = ({ id }) => {
  const { addToast } = useAppContext();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { dashboard, selectedDatarange } = useDashboards();
  const { data: runtimeData, loading: loadingData } = useWidgetRuntime(id, dashboard?._id, undefined, JSON.stringify(selectedDatarange));

  const [data, setData] = useState([]);
  const [general, setGeneral] = useState(null);
  const [widgetTitle, setWidgetTitle] = useState("");
  const [config, setConfig] = useState({});
  const [loadingInfo, setLoadingInfo] = useState(true);

  const graphType = config?.graphType || "line";
  const isBars = graphType === "VBars" || graphType === "HBars";
  const isHorizontal = graphType === "HBars";

  const { fixedColor = "#4287f5", nDecimals = 2 } = config;

  const hasChartData = data && data.length > 0;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const label = payload[0]?.payload?.label;
    const val = payload[0]?.value;
    const color = payload[0]?.color || fixedColor;

    return (
      <div className="bg-black text-white px-3 py-2 rounded text-sm opacity-90">
        <div className="font-semibold mb-1">{label}</div>
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span>
            Calidad: {Number(val || 0).toFixed(nDecimals)}%
          </span>
        </div>
      </div>
    );
  };

  const refreshWidgetInfo = async () => {
    try {
      setLoadingInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setWidgetTitle(response?.title || "");
      setConfig(response?.configuration || {});
      setLoadingInfo(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setLoadingInfo !== 'undefined') setLoadingInfo(false);
        return;
      }
      navigate('/logout');
    }
  };

  useEffect(() => {
    refreshWidgetInfo();
  }, [selectedDatarange]);

  // Los datos llegan por socket desde el singleton compartido del backend.
  useEffect(() => {
    setGeneral(runtimeData?.general || null);
    const groups = runtimeData?.byGroup || [];
    setData(groups.map(normalizeGroup));
  }, [runtimeData]);

  if (loadingData || loadingInfo) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="w-full h-full p-4 bg-white border border-gray-300 rounded flex flex-col gap-4 overflow-auto">
      <div className="text-center font-semibold text-gray-700">
        {widgetTitle}
      </div>

      {general && (
        <div className="flex gap-4 flex-wrap justify-center">
          <div
            className="rounded p-4 w-48 text-center text-white shadow-md"
            style={{ backgroundColor: fixedColor }}
          >
            <div className="text-sm font-semibold">Calidad General</div>
            <div className="text-3xl font-bold mt-1">
              {Number(general.quality || 0).toFixed(nDecimals)}%
            </div>
          </div>
        </div>
      )}

      {hasChartData && (
        <div className="w-full flex-grow min-h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            {isBars ? (
              <BarChart
                data={data}
                layout={isHorizontal ? "vertical" : "horizontal"}
                barGap={18}
                barCategoryGap={30}
                margin={{ top: 30, right: 40, bottom: 30, left: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                {isHorizontal ? (
                  <>
                    <XAxis type="number" domain={[0, (max) => Math.max(100, max + 8)]} />
                    <YAxis type="category" dataKey="label" width={120} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="label" />
                    <YAxis domain={[0, 100]} />
                  </>
                )}

                <Tooltip content={<CustomTooltip />} />

                <Bar
                  dataKey="quality"
                  name="Calidad"
                  maxBarSize={80}
                  radius={[6, 6, 0, 0]}
                  fill={fixedColor}
                >
                  <LabelList
                    dataKey="quality"
                    position={isHorizontal ? "right" : "top"}
                    formatter={(v) => `${Number(v || 0).toFixed(nDecimals)}%`}
                    fill="#1F2937"
                  />
                </Bar>
              </BarChart>
            ) : (
              <LineChart
                data={data}
                margin={{ top: 30, right: 40, bottom: 20, left: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis domain={[0, 100]} padding={{ top: 35 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="top" />

                <Line
                  type="monotone"
                  dataKey="quality"
                  name="Calidad"
                  stroke={fixedColor}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 7 }}
                >
                  <LabelList
                    dataKey="quality"
                    position="top"
                    fill="#1F2937"
                    formatter={(v) => `${Number(v || 0).toFixed(nDecimals)}%`}
                  />
                </Line>
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default QualityWidget;
